import axios from "axios";
import { useEffect, useState } from "react";

interface backResponseEl {
  temperatura?: number;
  temperatura_nasa?: number;
  conductividad?: number;
  turbidez?: number;
  salinidad?: number;
  profundidad?: number;
  orp?: number;
  ph?: number;
  clorofila?: number;
  oxigeno_disuelto?: number;
  oxigeno_saturacion?: number;
  nitrato?: number;
  amonio?: number;
  cianobacterias?: number;
  coliformes_termotolerantes?: number;
  enterococos_ecocos?: number;
  enterococos_mg5?: number;
  phycoerythtin?: number;
  temperatura_agua?: number;
  temperatura_ambiente?: number;
  presion_atmosferica?: number;
  precipitacion?: number;
  velocidad_viento?: number;
  direccion_viento?: number;
  velocidad_corriente?: number;
  direccion_corriente?: number;
  ola_altura_significativa?: number;
  ola_altura_maxima?: number;
  ola_direccion_media?: number;
  ola_periodo_pico?: number;
  bateria_porcentaje?: number;
  bateria_wh?: number;
  nivel_mar?: number;
  voltaje_device?: number;
  temperatura_device?: number;
  voltajeDevice?: number;
  temperaturaDevice?: number;
  latitud?: number;
  longitud?: number;
  timestamp?: string;
  source_timestamp?: string;
  source_status?: string;
  source_station_id?: string;
  vertical_datum?: string;
  vertical_datum_confirmed?: boolean;
  recvTime?: string | Date;
}

export interface DataResponse {
  temperatura?: number;
  temperatura_nasa?: number;
  conductividad?: number;
  turbidez?: number;
  salinidad?: number;
  profundidad?: number;
  orp?: number;
  ph?: number;
  clorofila?: number;
  oxigeno_disuelto?: number;
  oxigeno_saturacion?: number;
  nitrato?: number;
  amonio?: number;
  cianobacterias?: number;
  coliformes_termotolerantes?: number;
  enterococos_ecocos?: number;
  enterococos_mg5?: number;
  phycoerythtin?: number;
  temperatura_agua?: number;
  temperatura_ambiente?: number;
  presion_atmosferica?: number;
  precipitacion?: number;
  velocidad_viento?: number;
  direccion_viento?: number;
  velocidad_corriente?: number;
  direccion_corriente?: number;
  ola_altura_significativa?: number;
  ola_altura_maxima?: number;
  ola_direccion_media?: number;
  ola_periodo_pico?: number;
  bateria_porcentaje?: number;
  bateria_wh?: number;
  nivel_mar?: number;
  voltaje_device?: number;
  temperatura_device?: number;
  latitud?: number;
  longitud?: number;
  timestamp?: string;
  source_timestamp?: string;
  source_status?: string;
  source_station_id?: string;
  vertical_datum?: string;
  vertical_datum_confirmed?: boolean;
  recvTime: Date;
}

export type GeneralServiceResponse = DataResponse[];

export interface GeneralServicePayload {
  data: GeneralServiceResponse;
  reduced: boolean;
}

export function useGetGeneralData(
  context: string,
  fecha_desde: string,
  fecha_hasta: string,
): GeneralServicePayload {
  const [payload, setPayload] = useState<GeneralServicePayload>({ data: [], reduced: false });

  const mapResponseData = (responseData: backResponseEl[]): GeneralServiceResponse => {
    const dataResponse: GeneralServiceResponse = [];
    responseData.forEach((dataBack: backResponseEl) => {
      const timeField = dataBack.recvTime || dataBack.timestamp;
      const recv = timeField ? new Date(timeField) : new Date();
      const newDataEl: DataResponse = {
        temperatura: dataBack.temperatura,
        temperatura_nasa: dataBack.temperatura_nasa,
        conductividad: dataBack.conductividad,
        turbidez: dataBack.turbidez,
        salinidad: dataBack.salinidad,
        profundidad: dataBack.profundidad,
        orp: dataBack.orp,
        ph: dataBack.ph,
        clorofila: dataBack.clorofila,
        oxigeno_disuelto: dataBack.oxigeno_disuelto,
        oxigeno_saturacion: dataBack.oxigeno_saturacion,
        nitrato: dataBack.nitrato,
        amonio: dataBack.amonio,
        cianobacterias: dataBack.cianobacterias,
        coliformes_termotolerantes: dataBack.coliformes_termotolerantes,
        enterococos_ecocos: dataBack.enterococos_ecocos,
        enterococos_mg5: dataBack.enterococos_mg5,
        temperatura_agua: dataBack.temperatura_agua,
        temperatura_ambiente: dataBack.temperatura_ambiente,
        presion_atmosferica: dataBack.presion_atmosferica,
        precipitacion: dataBack.precipitacion,
        velocidad_viento: dataBack.velocidad_viento,
        direccion_viento: dataBack.direccion_viento,
        velocidad_corriente: dataBack.velocidad_corriente,
        direccion_corriente: dataBack.direccion_corriente,
        ola_altura_significativa: dataBack.ola_altura_significativa,
        ola_altura_maxima: dataBack.ola_altura_maxima,
        ola_direccion_media: dataBack.ola_direccion_media,
        ola_periodo_pico: dataBack.ola_periodo_pico,
        bateria_porcentaje: dataBack.bateria_porcentaje,
        bateria_wh: dataBack.bateria_wh,
        nivel_mar: dataBack.nivel_mar,
        source_timestamp: dataBack.source_timestamp,
        source_status: dataBack.source_status,
        source_station_id: dataBack.source_station_id,
        vertical_datum: dataBack.vertical_datum,
        vertical_datum_confirmed: dataBack.vertical_datum_confirmed,
        voltaje_device: dataBack.voltaje_device ?? dataBack.voltajeDevice,
        temperatura_device: dataBack.temperatura_device ?? dataBack.temperaturaDevice,
        latitud: dataBack.latitud,
        longitud: dataBack.longitud,
        timestamp: dataBack.timestamp,
        recvTime: recv,
      };
      dataResponse.push(newDataEl);
    });
    return dataResponse;
  };

  useEffect(() => {
    if (fecha_desde && fecha_hasta) {
      let didCancel = false;
      const loadData = async () => {
        const baseUrl = process.env.REACT_APP_API_HOST ?? "";
        try {
          const response = await axios.get(`${baseUrl}/api/v1/contexts/${context}`, {
            headers: { "Access-Control-Allow-Origin": "*" },
            params: { fecha_desde: fecha_desde, fecha_hasta: fecha_hasta },
          });
          if (didCancel) return;
          const responseData = response.data.data || [];
          const mapped = mapResponseData(responseData);

          if (mapped.length === 0) {
            try {
              const lastResp = await axios.get(`${baseUrl}/api/v1/contexts/${context}/last`);
              if (didCancel) return;
              const lastData = mapResponseData(lastResp.data.data || []);
              setPayload({ data: lastData, reduced: Boolean(lastResp.data.reduced) });
            } catch (err) {
              console.log("error fetching last measurement", err);
              setPayload({ data: mapped, reduced: Boolean(response.data.reduced) });
            }
          } else {
            setPayload({ data: mapped, reduced: Boolean(response.data.reduced) });
          }
        } catch (error) {
          console.log("error", error);
        }
      };

      loadData();
      return () => {
        didCancel = true;
      };
    }
  }, [context, fecha_desde, fecha_hasta]);
  return payload;
}
